/**
 * DbFeatureToggles — Database-backed feature toggles for the Settings page.
 * Fetches toggles from config_settings, renders toggle switches with
 * real-time WebSocket updates.
 * Extracted from Settings.tsx for maintainability.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Flag, ToggleLeft, ToggleRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import productionService from '../services/productionService';
import { wsService } from '../services/websocket';

const DbFeatureToggles: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // Fetch all config_settings and extract DB toggles
  const { data: dbToggles = {} } = useQuery<Record<string, boolean>>(
    'db-feature-toggles',
    async () => {
      const all = await productionService.getConfigSettings();
      const toggleKeys = ['enable_input_output_editing', 'require_post_print_scan'];
      const result: Record<string, boolean> = {};
      for (const key of toggleKeys) {
        const setting = all.find(s => s.setting_key === key);
        result[key] = setting?.setting_value === 'true';
      }
      return result;
    }
  );

  // Listen for real-time config changes via WebSocket
  React.useEffect(() => {
    wsService.connect();
    const handleConfigUpdate = () => {
      queryClient.invalidateQueries('db-feature-toggles');
      queryClient.invalidateQueries('enable-input-output-editing');
    };
    wsService.on('config-updated', handleConfigUpdate);
    return () => { wsService.off('config-updated', handleConfigUpdate); };
  }, [queryClient]);

  // Mutation to toggle a setting
  const toggleMutation = useMutation(
    async ({ key, value }: { key: string; value: boolean }): Promise<void> => {
      await productionService.updateConfigSetting(key, String(value));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('db-feature-toggles');
        queryClient.invalidateQueries('enable-input-output-editing');
        toast.success(t('common.success'));
      },
      onError: () => { toast.error(t('common.error')); },
    }
  );

  const toggleItems = [
    {
      key: 'enable_input_output_editing',
      label: t('admin.enableInputOutputEditing'),
      description: t('admin.enableInputOutputEditingDesc'),
      color: 'text-orange-500',
    },
    {
      key: 'require_post_print_scan',
      label: t('admin.requirePostPrintScan'),
      description: t('admin.requirePostPrintScanDesc'),
      color: 'text-blue-500',
    },
  ];

  return (
    <div className="mt-6">
      <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
        {t('admin.databaseToggles')}
      </h3>
      <p className="text-sm text-secondary-500 dark:text-gray-400 mb-4">
        {t('admin.databaseTogglesDesc')}
      </p>
      <div className="space-y-4">
        {toggleItems.map((item) => {
          const isOn = dbToggles[item.key] ?? false;
          return (
            <div key={item.key} className="flex items-center justify-between py-4 px-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <Flag className={`w-5 h-5 ${item.color}`} />
                <div>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">{item.label}</p>
                  <p className="text-xs text-secondary-500 dark:text-gray-400">{item.description}</p>
                </div>
              </div>
              <button
                onClick={() => toggleMutation.mutate({ key: item.key, value: !isOn })}
                disabled={toggleMutation.isLoading}
                className={`flex-shrink-0 ${isOn ? 'text-green-600' : 'text-gray-400'} hover:opacity-80 transition-opacity disabled:opacity-50`}
              >
                {isOn ? <ToggleRight className="w-10 h-10" /> : <ToggleLeft className="w-10 h-10" />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DbFeatureToggles;
